import { Bounds } from 'src/modules/utils/shared/bounds.model';
import { Point } from 'src/modules/utils/shared/point.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldAddon } from './frontend-field-addon.model';
import { FrontendFieldGuiHint } from './frontend-field-gui-hint.model';
import { FrontendFieldListItem } from './frontend-field-list-item.model';
import { FrontendFieldType } from './frontend-field-type.enum';
import { FrontendFormDefinition } from './frontend-form-definition.model';
import { TableColumn } from './table-column.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { EnaioWorkItemParameter } from 'src/modules/enaio/shared/EnaioWorkItemParameter';
import { Type } from 'class-transformer';
import { SmTableCellEditorComponent } from '../components/sm-table-cell-editor/sm-table-cell-editor.component';
import { TableCellType } from './table-cell-type.enum';

export class FrontendFieldDefinition {

    id = "";
    caption = "";
    type: FrontendFieldType = FrontendFieldType.text;
    tabOrder = 0;
    mandatory = false;
    fixedLabelBounds: Bounds = null;
    fixedControlBounds: Bounds = null;
    hasLabel = true;
    hasControl = true;
    tooltip: string = null;
    tooltipStyleClasses: string = null;
    tooltipOnControl = false;
    maxLength: number = null;
    monospaced = false;
    minValue = 0;
    maxValue: number = null;
    minValueInclusive = true;
    maxValueInclusive = true;
    upperCase = false;
    labelRightAligned = false;
    @Type(() => FrontendFieldListItem)
    listItems: FrontendFieldListItem[] = [];
    listItemValuePath: string;
    dataSourceId: string;
    
    children: FrontendFieldDefinition[] = [];

    @Type(() => TableColumn)
    tableColumns: TableColumn[] = [];
    tableCanAddRow = true;
    tableCanRemoveRow = true;
    tableAllowRowDrag = false;
    tableAddRowOnTabInLastCell = false;
    tableDisabledCells: { row: number; column: number }[];
    tableInvalidCells: OrdinaryObject<string> = {};
    tableMustRefresh = false;
    fullScreenRowEditorOnMobile = false;
    
    regexMask = "";
    isEmailAddress = false;
    radioButtonGroup = "";
    radioButtonValue: any = "";
    dropdownEditable = true;
    dropdownCanFilter = false;
    dropdownAutoComplete = false;
    dropdownButtonVisible = true;
    dropdownWordWrap = false;
    listBoxCheckBoxes = false;
    passwordFeedback = true;
    predefinedColors: number[];
    datePickerWithTime = false;
    datePickerMonth = false;
    datePickerYear = false;
    indent = 0;

    dateIntervalToField = "";
    dateIntervalFromField = "";
    dateIntervalSameYear = false;
    dateIntervalSameMonth = false;

    fixedHeight: string = null;

    visible = true;
    enabled = true;

    enaioSectionName = "";

    guiGroup = "";
    guiWidthInGroup = 100;
    guiGroupOwnLabel: boolean = null;
    guiHints: FrontendFieldGuiHint[] = [];
    addOnButtons: FrontendFieldAddon[] = null;
    
    style: OrdinaryObject;

    componentType: any = null; // type = Type<any>
    enaioSource: any = null; //EnaioGuiField = null;
    
    value: any = null;

    requestFocus = false;

    pageSelected = 0;

    //Keine wirklich schöne Lösung mit dem Request, aber keine bessere Idee zur Zeit
    tableSelectedRow = -1;
    tableSelectedRowRequest = -1;
    
    noMapping = false;

    context: any;
    component: any;
    tableCellEditor: SmTableCellEditorComponent;
    
    errorMessage: string = null;
    errorMessageFromScript: string = null;

    userDefinedData: OrdinaryObject;

    generateEmptyTableItem: () => {};

    validator: (field: FrontendFieldDefinition, form: FrontendFormDefinition) => string;

    onAutoComplete: (query: string) => Promise<FrontendFieldListItem[]>;
    onClick: (event: MouseEvent) => any;
    onValueChanged: (byUser: boolean) => any;
    onBeforeAddRow: () => any;
    onAddOnClick: (item: FrontendFieldAddon, event: MouseEvent) => any;

    toForm: (value: any) => any;

    constructor(id: string, caption: string, type: FrontendFieldType, props: Partial<FrontendFieldDefinition> = null) {
        this.id = id;
        this.caption = caption;
        this.type = type;
        if (props != null) {
            Object.assign(this, props);
        }
    }

    getDescendents(): FrontendFieldDefinition[] {
        return Utils.arrayExplode(this.children, child => [child, ...child.getDescendents()]);
    }

    getGroupedChildren(): FrontendFieldDefinition[][] {
        let result: FrontendFieldDefinition[][] = [];
        for (let field of this.children) {
            let group = Utils.isNoe(field.guiGroup) ? null : result.find(g => g[0].guiGroup == field.guiGroup);
            if (group == null) {
                result.push([field]);
            }
            else {
                group.push(field);
            }
        }
        return result;
    }

    setComponent(component: any /* type = Type<any>*/): this {
        this.componentType = component;
        return this;
    }

    setCellValid(row: number, column: number, valid: boolean, errorMessage?: string): void {
        let key = row + "," + column;
        if (valid) {
            delete this.tableInvalidCells[key];
        }
        else {
            this.tableInvalidCells[key] = errorMessage;
        }
    }

    isCellValid(row: number, column: number): boolean {
        return !Utils.hasProperty(this.tableInvalidCells, row + "," + column);
    }

    getCellErrorMessage(row: number, column: number): string {
        return Utils.getSafe(this.tableInvalidCells, row + "," + column);
    }

    getCellStyleClass(value: any, row: number, column: number): string {
        return !this.isCellValid(row, column) ? "xbg-red-100" : !this.isTableCellEnabled(row, column) ? "xbg-gray-100" : null;
    }

    getErrorMessage(withCalculated = true, forToolTip = false): string {
        let result = Utils.stringDef(withCalculated ? this.errorMessage : null, this.errorMessageFromScript);
        if (!forToolTip && Utils.isNoe(result)) {
            let invalidCells = Utils.getOwnPropertyNames(this.tableInvalidCells);
            if (invalidCells.length > 0) {
                let pos = Utils.splitOnce(invalidCells[0], ",");
                result = (Utils.arrayGetSafe(this.tableColumns, Utils.toNumber(pos[1]))?.caption ?? pos[1]) + ", Zeile  " + (Utils.toNumber(pos[0]) + 1) + ": " + this.tableInvalidCells[invalidCells[0]];
            }
        }
        return result;
    }

    isFixedPosition(): boolean {
        return this.fixedLabelBounds != null || this.fixedControlBounds != null;
    }

    getMaxPosition(): Point {
        let result = new Point(0, 0);
        if (this.fixedLabelBounds != null) {
            result = result.max(this.fixedLabelBounds.getRight(), this.fixedLabelBounds.getBottom());
        }
        if (this.fixedControlBounds != null) {
            result = result.max(this.fixedControlBounds.getRight(), this.fixedControlBounds.getBottom());
        }
        return result;
    }

    updateValueFromGui(): void {
        if (this.component != null) {
            this.value = this.component.getValue();
        }
    }

    getUpdatedValue(): any {
        this.updateValueFromGui();
        return this.value;
    }

    isGuiOneLine(): boolean {
        return this.type == FrontendFieldType.checkBox || this.type == FrontendFieldType.radioButton;
    }

    getFrontendValueForWorkItem(param: EnaioWorkItemParameter): any {
        let value = param.getPlainValue();
        if (this.type == FrontendFieldType.checkBox) {
            return Utils.toBool(value);
        }
        else {
            return value;
        }
    }

    validate(form: FrontendFormDefinition): string {
        if (this.visible) {
            if (this.mandatory && Utils.isNoe(this.value)) {
                return this.caption + " ist ein Pflichtfeld";
            }
            if (this.minValue != null && (this.minValueInclusive ? this.value < this.minValue : this.value <= this.minValue)) {
                return this.caption + " muss größer " + (this.minValueInclusive ? "oder gleich " : "") + this.minValue + " sein";
            }
            if (this.maxValue != null && (this.maxValueInclusive ? this.value > this.maxValue : this.value >= this.maxValue)) {
                return this.caption + " muss kleiner " + (this.maxValueInclusive ? "oder gleich " : "") + this.maxValue + " sein";
            }
            if (this.isEmailAddress && !Utils.isNoe(this.value) && !Utils.isEmailAddressValid(this.value)) {
                return this.caption + " ist keine gültige E-Mail-Adresse";
            }
            if (this.validator != null) {
                let result = this.validator(this, form);
                if (result != null) {
                    return result;
                }
            }
            if (!Utils.isNoe(this.dateIntervalToField)) {
                let toField = form.getField(this.dateIntervalToField);
                if (toField.visible) {
                    let toValue = form.getValue(this.dateIntervalToField);
                    let thisValue = this.getValue();

                    if (toValue instanceof Date && thisValue instanceof Date) {
                        if (thisValue > toValue) {
                            return this.caption + " darf nicht nach " + form.getField(this.dateIntervalToField).caption + " liegen";
                        }
                        if (this.dateIntervalSameYear && thisValue.getFullYear() != toValue.getFullYear()) {
                            return this.caption + " muss im selben Kalenderjahr wie " + form.getField(this.dateIntervalToField).caption + " liegen";
                        }
                        if (this.dateIntervalSameMonth && (thisValue.getFullYear() != toValue.getFullYear() || thisValue.getMonth() != toValue.getMonth())) {
                            return this.caption + " muss im selben Kalendermonat wie " + form.getField(this.dateIntervalToField).caption + " liegen";
                        }
                    }
                }
            }
            if (!Utils.isNoe(this.dateIntervalFromField)) {
                let fromField = form.getField(this.dateIntervalFromField);
                if (fromField.visible) {
                    let fromValue = form.getValue(this.dateIntervalFromField);
                    let thisValue = this.getValue();

                    if (fromValue instanceof Date && thisValue instanceof Date) {
                        if (thisValue < fromValue) {
                            return this.caption + " darf nicht vor " + form.getField(this.dateIntervalFromField).caption + " liegen";
                        }
                        if (this.dateIntervalSameYear && thisValue.getFullYear() != fromValue.getFullYear()) {
                            return this.caption + " muss im selben Kalenderjahr wie " + form.getField(this.dateIntervalFromField).caption + " liegen";
                        }
                        if (this.dateIntervalSameMonth && (thisValue.getFullYear() != fromValue.getFullYear() || thisValue.getMonth() != fromValue.getMonth())) {
                            return this.caption + " muss im selben Kalendermonat wie " + form.getField(this.dateIntervalFromField).caption + " liegen";
                        }
                    }
                }
            }
            return this.getErrorMessage(false);
        }
        return null;
    }

    setValue(value: any): void {
        this.value = this.toForm != null ? this.toForm(value) : value;
        if (this.type == FrontendFieldType.comboBoxMulti && this.dropdownAutoComplete && Utils.isArray(this.value)) {
            this.value = (this.value as any[]).map(item => this.listItems.find(li => item == li.value));
        }
        else if (this.type == FrontendFieldType.comboBox && !this.dropdownEditable && !(value instanceof FrontendFieldListItem)) {
            this.value = this.listItemValuePath == null || value == null ? value : Utils.getPropertyNested(value, this.listItemValuePath);
        }
    }

    getValue(useDropdownInternalObject = true): any {
        if (this.type == FrontendFieldType.datePicker && !this.datePickerWithTime && this.value instanceof Date) {
            return Utils.dateWithoutTime(this.value);
        }
        else if (this.type == FrontendFieldType.comboBox) {
            if (this.value instanceof FrontendFieldListItem) {
                return useDropdownInternalObject ? this.value.internalObject ?? this.value.value : this.value.value;
            }
            else {
                return this.listItemValuePath == null ? this.value : this.listItems.find(item => Utils.getPropertyNested(item.internalObject, this.listItemValuePath) == this.value)?.internalObject;
            }
        }
        else if (this.type == FrontendFieldType.checkBox && this.value == null) {
            return false;
        }
        else {
            return this.value;
        }
    }

    isTableCellEnabled(row: number, column: number): boolean {
        return this.tableDisabledCells == null || !this.tableDisabledCells.some(cell => cell.row == row && cell.column == column);
    }

    hasLabelAdvanced(): boolean {
        return this.hasLabel && FrontendFieldType.hasLabel(this.type);
    }

    toTableColumn(): TableColumn {
        let result = new TableColumn(this.id, this.caption, TableCellType.text, { flex: 1 });
        switch (this.type) {
            case FrontendFieldType.int:
                result.cellType = TableCellType.int;
                break;
            case FrontendFieldType.decimal:
                result.cellType = TableCellType.decimal;
                break;
            case FrontendFieldType.checkBox:
                result.cellType = TableCellType.yesNo;
                break;
            case FrontendFieldType.datePicker:
                result.cellType = TableCellType.date;
                break;
        }
        return result;
    }

}
